import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { formatCurrency } from '../../lib/format';
import {
  previewReparatieOfferte,
  sendReparatieQuote,
  type OffertePreview,
  type ReparatieOpdracht,
} from '../../lib/reparatie';

interface Props {
  opdracht: ReparatieOpdracht;
  onClose: () => void;
  onSent: () => void;
}

/**
 * Voorstel naar de klant: eerst zien wat er uitgaat, dan pas versturen.
 *
 * De preview komt uit dezelfde kandidaat + renderer als het verzendpad, zodat
 * het voorbeeld niet uit de pas kan lopen met de echte mail. Staat `S21` nog
 * niet in `COMMS_MAIL_TYPES`, dan mailt Salesforce met de VF-template en zegt
 * de banner dat dit een voorbeeld van het eigen pad is — een preview die iets
 * anders toont dan wat de klant krijgt is erger dan geen preview.
 */
export function ReparatieOffertePreviewDialog({ opdracht, onClose, onSent }: Props) {
  const [eigenBericht, setEigenBericht] = useState('');
  const [preview, setPreview] = useState<OffertePreview | null>(null);
  const [laden, setLaden] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const haalPreview = useCallback(
    async (bericht: string) => {
      setLaden(true);
      setError(null);
      try {
        const res = await previewReparatieOfferte({
          opdrachtId: opdracht.id,
          eigenBericht: bericht.trim() || null,
        });
        if (!res.success) setError(res.message || 'Voorbeeld maken mislukt');
        setPreview(res.success ? res : null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Voorbeeld maken mislukt');
      } finally {
        setLaden(false);
      }
    },
    [opdracht.id]
  );

  useEffect(() => {
    haalPreview('');
  }, [haalPreview]);

  // Live meelezen terwijl je typt, maar niet per toetsaanslag een render.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => haalPreview(eigenBericht), 500);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eigenBericht]);

  const handleSend = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await sendReparatieQuote(opdracht.id, eigenBericht.trim() || null);
      if (!res.success) {
        setError(res.message || 'Versturen mislukt');
        setBusy(false);
        return;
      }
      onSent();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Versturen mislukt');
      setBusy(false);
    }
  };

  const apexModus = preview?.modus === 'apex';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold text-slate-900">Voorstel naar de klant</h2>
          <p className="text-xs text-slate-500 mt-1">
            {opdracht.opdrachtNummer}
            {preview?.to ? ` · naar ${preview.to}` : ''}
            {opdracht.verkoopBedrag != null ? ` · ${formatCurrency(opdracht.verkoopBedrag)} incl. BTW` : ''}
          </p>
        </div>

        <div className="px-6 py-4 space-y-3 overflow-y-auto">
          {apexModus && (
            <div className="text-xs rounded px-2.5 py-2 border bg-amber-50 border-amber-200 text-amber-900">
              <span className="font-medium">Let op: </span>
              deze mail wordt op dit moment door Salesforce verstuurd met de oude sjabloon. Wat je
              hieronder ziet is de nieuwe versie; de klant krijgt nog de oude, en je toelichting
              gaat dan niet mee. Zet <code>S21</code> in <code>COMMS_MAIL_TYPES</code> om dit om te
              zetten — dan komt de mail ook in de klant-tijdlijn te staan.
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Eigen toelichting <span className="text-slate-400 font-normal">(optioneel, komt boven het bedrag)</span>
            </label>
            <textarea
              rows={3}
              maxLength={600}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
              value={eigenBericht}
              onChange={(e) => setEigenBericht(e.target.value)}
              disabled={busy}
              placeholder="Bijv. De vakman adviseert alle randen rondom opnieuw om te woelen; alleen de losse plekken herstellen houdt niet."
            />
          </div>

          <div>
            <p className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1">
              Voorbeeld {laden && <span className="text-slate-400 normal-case font-normal">— bijwerken…</span>}
            </p>
            {preview?.subject && (
              <p className="text-xs text-slate-600 mb-2">
                <span className="text-slate-400">Onderwerp: </span>
                {preview.subject}
              </p>
            )}
            {preview?.html ? (
              <iframe
                title="Voorbeeld van de mail"
                srcDoc={preview.html}
                sandbox=""
                className="w-full h-[45vh] border border-slate-200 rounded bg-white"
              />
            ) : (
              !laden && <p className="text-sm text-slate-500">Geen voorbeeld beschikbaar.</p>
            )}
          </div>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Terug
          </button>
          <button
            type="button"
            onClick={handleSend}
            disabled={busy || laden}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              busy || laden
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {busy ? 'Versturen…' : 'Verstuur naar klant'}
          </button>
        </div>
      </div>
    </div>
  );
}
